import React, { createContext, useContext, useState } from 'react';
import { cn } from '../../lib/utils';

// Interfaces pour les options et props
interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  hoverColor?: string;
  selectedColor?: string;
}

interface SelectContextProps {
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  hoverColor?: string;
  selectedColor?: string;
}

// Contexte pour le composant Select
const SelectContext = createContext<SelectContextProps | undefined>(undefined);

// Hook pour utiliser le contexte Select
const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('Les composants Select doivent être utilisés à l\'intérieur d\'un composant Select');
  }
  return context;
};

// Composant Select principal
export const Select: React.FC<SelectProps> = ({ 
  value, 
  onValueChange, 
  children,
  className,
  disabled = false,
  hoverColor,
  selectedColor,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  
  return (
    <SelectContext.Provider value={{ value, onValueChange, open, setOpen, hoverColor, selectedColor }}>
      <div className={cn("relative", className)} {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

// Composant Trigger pour le Select
interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children?: React.ReactNode;
  onFocusStyle?: React.CSSProperties;
  onBlurStyle?: React.CSSProperties;
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ 
  className, 
  children,
  onFocusStyle,
  onBlurStyle,
  style,
  ...props 
}) => {
  const { open, setOpen, value } = useSelectContext();
  const triggerId = `select-trigger-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center justify-between w-full rounded-lg border px-3 py-2 text-sm transition-all duration-200 focus:outline-none",
          triggerId,
          className
        )}
        style={style}
        {...props}
      >
        {children}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className={cn("h-4 w-4 transition-transform duration-200 ml-2 flex-shrink-0", open ? "rotate-180" : "")}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {(onFocusStyle || onBlurStyle) && (
        <style>{`
          ${onFocusStyle ? `.${triggerId}:focus { 
            ${Object.entries(onFocusStyle).map(([key, value]) => 
              `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`
            ).join(' ')}
          }` : ''}
        `}</style>
      )}
    </>
  );
};

// Composant Value pour le Select
interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, children }) => {
  const { value } = useSelectContext();
  
  return (
    <span>{children || value || placeholder}</span>
  );
};

// Composant Content pour le Select
interface SelectContentProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const SelectContent: React.FC<SelectContentProps> = React.memo(({ className, children, style }) => {
  const { open, setOpen } = useSelectContext();
  
  // Utilisation de useEffect pour ajouter un gestionnaire d'événements global
  React.useEffect(() => {
    if (!open) return;
    
    const handleClickOutside = () => {
      setOpen(false);
    };
    
    // Ajouter l'événement après un court délai pour éviter la fermeture immédiate
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);
    
    // Nettoyer l'événement lors du démontage
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [open, setOpen]);
  
  if (!open) return null;
  
  return (
    <div className="relative z-50" onClick={(e) => e.stopPropagation()}>
      <div className="fixed inset-0" onClick={() => setOpen(false)} />
      <div 
        className="absolute top-full left-0 right-0 mt-1 overflow-hidden rounded-md border shadow-lg max-h-60 overflow-y-auto"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.15)',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(12px)',
          willChange: 'transform',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          ...style
        }}
      >
        <style>{`
          .absolute.top-full::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <div className={cn("py-1", className)}>
          {children}
        </div>
      </div>
    </div>
  );
});

// Composant Item pour le Select
interface SelectItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  className?: string;
}

export const SelectItem: React.FC<SelectItemProps> = ({ 
  value: itemValue, 
  className, 
  children,
  ...props 
}) => {
  const { value, onValueChange, setOpen, hoverColor, selectedColor } = useSelectContext();
  
  const handleSelect = () => {
    onValueChange(itemValue);
    setOpen(false);
  };
  
  const isSelected = value === itemValue;
  const itemId = `select-item-${itemValue.replace(/[^a-zA-Z0-9]/g, '-')}`;
  
  return (
    <>
      <button
        type="button"
        onClick={handleSelect}
        className={cn(
          "flex w-full items-center px-3 py-2 text-base text-white transition-colors duration-150 font-semibold",
          !hoverColor && "hover:bg-white/10",
          !selectedColor && isSelected && "bg-yellow-500/20 text-yellow-400",
          itemId,
          className
        )}
        style={{
          backgroundColor: isSelected && selectedColor ? selectedColor : undefined
        }}
        {...props}
      >
        {children}
      </button>
      {hoverColor && (
        <style>{`
          .${itemId}:hover {
            background-color: ${hoverColor} !important;
          }
        `}</style>
      )}
    </>
  );
};

// Composant simple pour la compatibilité avec l'ancien code
interface SimpleSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
}

export const SimpleSelect: React.FC<SimpleSelectProps> = ({ 
  value, 
  onValueChange, 
  options,
  className,
  ...props
}) => {
  return (
    <select
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      className={cn(
        "block w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white ring-offset-white transition-colors placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        className
      )}
      {...props}
    >
      {options.map((option) => (
        <option 
          key={option.value} 
          value={option.value}
          className="bg-gray-900 text-white"
        >
          {option.label}
        </option>
      ))}
    </select>
  );
};
